import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import axios from 'axios';
import { toast } from 'sonner';

const InquiryPopup = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    product_interest: '',
    monthly_requirement: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    // Check if popup has been shown in last 7 days
    const lastShown = localStorage.getItem('inquiryPopupLastShown');
    const now = Date.now();
    const sevenDays = 7 * 24 * 60 * 60 * 1000;

    if (!lastShown || (now - parseInt(lastShown)) > sevenDays) {
      // Show popup after 15 seconds
      const timer = setTimeout(() => {
        setIsOpen(true);
        localStorage.setItem('inquiryPopupLastShown', now.toString());
      }, 15000);

      return () => clearTimeout(timer);
    }

    // Exit intent detection (desktop only)
    const handleMouseLeave = (e) => {
      if (e.clientY <= 0 && !lastShown) {
        setIsOpen(true);
        localStorage.setItem('inquiryPopupLastShown', now.toString());
      }
    };

    document.addEventListener('mouseleave', handleMouseLeave);
    return () => document.removeEventListener('mouseleave', handleMouseLeave);
  }, []);

  const handleChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Using FormSubmit.co - free form submission service (no registration needed)
      const formDataToSend = new FormData();
      
      formDataToSend.append('name', formData.name);
      formDataToSend.append('Company', formData.company);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('Phone', formData.phone);
      formDataToSend.append('Product Interest', formData.product_interest);
      formDataToSend.append('Monthly Requirement', formData.monthly_requirement);
      formDataToSend.append('message', formData.message);
      formDataToSend.append('_subject', 'New Inquiry from Big Bag Essentials Website');
      formDataToSend.append('_template', 'table');
      formDataToSend.append('_captcha', 'false');

      const response = await fetch('https://formsubmit.co/ajax/anandsharma@bigbagessentials.in', {
        method: 'POST',
        body: formDataToSend
      });

      const data = await response.json();
      
      if (data.success === 'true' || response.ok) {
        toast.success('Inquiry submitted successfully! We\'ll get back to you within 24 hours.');
        setIsOpen(false);
        setFormData({
          name: '',
          company: '',
          email: '',
          phone: '',
          product_interest: '',
          monthly_requirement: '',
          message: ''
        });
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      toast.error('Failed to submit inquiry. Please contact us at anandsharma@bigbagessentials.in');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="sm:max-w-[500px]" data-testid="inquiry-popup-open">
        <DialogHeader>
          <DialogTitle className="text-2xl">Get a Quote in 24 Hours</DialogTitle>
          <DialogDescription>
            Fill out the form below and our team will get back to you with pricing and specifications.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label htmlFor="popup-name" className="text-sm font-medium">
                Name *
              </label>
              <Input
                id="popup-name"
                name="name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                required
                placeholder="Your name"
                data-testid="inquiry-popup-name-input"
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="popup-company" className="text-sm font-medium">
                Company *
              </label>
              <Input
                id="popup-company"
                name="company"
                value={formData.company}
                onChange={(e) => handleChange('company', e.target.value)}
                required
                placeholder="Company name"
                data-testid="inquiry-popup-company-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label htmlFor="popup-email" className="text-sm font-medium">
                Email *
              </label>
              <Input
                id="popup-email"
                name="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                required
                placeholder="your@email.com"
                data-testid="inquiry-popup-email-input"
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="popup-phone" className="text-sm font-medium">
                Phone *
              </label>
              <Input
                id="popup-phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                required
                placeholder="+91 XXXXXXXXXX"
                data-testid="inquiry-popup-phone-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label htmlFor="popup-product" className="text-sm font-medium">
              Product Interest
            </label>
            <Select
              value={formData.product_interest}
              onValueChange={(value) => handleChange('product_interest', value)}
            >
              <SelectTrigger data-testid="inquiry-popup-product-select">
                <SelectValue placeholder="Select a product" />
              </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="PP Textured Yarn">PP Textured Yarn</SelectItem>
                            <SelectItem value="PP Filler Cord / Dust Proof Cord">PP Filler Cord / Dust Proof Cord</SelectItem>
                            <SelectItem value="PP Crimp Felt">PP Crimp Felt</SelectItem>
                            <SelectItem value="Braided Ropes">Braided Ropes</SelectItem>
                            <SelectItem value="B-Locks">B-Locks</SelectItem>
                            <SelectItem value="Other">Other</SelectItem>
                          </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label htmlFor="popup-requirement" className="text-sm font-medium">
              Monthly Requirement
            </label>
            <Input
              id="popup-requirement"
              name="monthly_requirement"
              value={formData.monthly_requirement}
              onChange={(e) => handleChange('monthly_requirement', e.target.value)}
              placeholder="e.g., 5000 kg"
              data-testid="inquiry-popup-requirement-input"
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="popup-message" className="text-sm font-medium">
              Message *
            </label>
            <Textarea
              id="popup-message"
              name="message"
              value={formData.message}
              onChange={(e) => handleChange('message', e.target.value)}
              required
              placeholder="Tell us about your requirements..."
              rows={3}
              data-testid="inquiry-popup-message-input"
            />
          </div>

          <Button 
            type="submit" 
            className="w-full" 
            size="lg" 
            disabled={isSubmitting}
            data-testid="inquiry-form-submit-button"
          >
            {isSubmitting ? 'Submitting...' : 'Submit Inquiry'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default InquiryPopup;
